import { useCallback, useEffect, useRef, useState } from 'react'
import { supabase } from '../lib/supabase'
import { useAuth } from '../lib/auth'
import { useLatestRef } from './useLatestRef'

export const DRAFT_PREFIX = 'fwdslash-draft-'

/**
 * Persist a draft to Supabase, keeping the localStorage copy as a safety
 * net until the server confirms.
 *
 * supabase-js does NOT reject on a regular query failure — it resolves
 * with `{ data, error }` and we have to inspect `error` ourselves. The
 * previous `try/await/catch` dance only caught network-level throws
 * (exceedingly rare); any RLS denial or constraint violation silently
 * wiped the local fallback because the catch block never ran. See PR 2
 * of the code-review handoff.
 *
 * Returns true on success, false on any failure — the caller can surface
 * a toast if they want, but we don't throw here because draft persistence
 * is fire-and-forget from user-facing code.
 */
// Exported for unit tests (see useDraft.persist.test.ts). Private in
// practice — consumers only interact via the `useDraft` hook.
export async function persistDraft(userId: string, urn: string, text: string): Promise<boolean> {
  if (!text.trim()) {
    // Delete path: only remove the local fallback AFTER the server
    // confirms. If Supabase rejects, we keep the local copy so the user
    // hasn't silently lost their text.
    try {
      const { error } = await supabase
        .from('drafts')
        .delete()
        .eq('user_id', userId)
        .eq('conversation_urn', urn)
      if (error) return false
      localStorage.removeItem(DRAFT_PREFIX + urn)
      return true
    } catch {
      // Network-level throw (offline, DNS, etc.) — keep local backup.
      return false
    }
  }

  // Save path: write localStorage FIRST as instant backup, then upsert.
  // Only drop the local copy once Supabase confirms the row landed.
  localStorage.setItem(DRAFT_PREFIX + urn, text)
  try {
    const { error } = await supabase.from('drafts').upsert(
      { user_id: userId, conversation_urn: urn, text, updated_at: new Date().toISOString() },
      { onConflict: 'user_id,conversation_urn' }
    )
    if (error) return false
    localStorage.removeItem(DRAFT_PREFIX + urn)
    return true
  } catch {
    // Network-level throw — local backup remains, next persist attempt
    // will try again.
    return false
  }
}

export function useDraft(conversationUrn: string | null) {
  const { user } = useAuth()
  const [draft, setDraft] = useState('')
  const [loaded, setLoaded] = useState(false)
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const currentTextRef = useRef('')
  const dirtyRef = useRef(false)
  const prevUrnRef = useRef<string | null>(null)
  const userRef = useLatestRef(user)

  // Load draft + flush previous conversation's draft
  useEffect(() => {
    // Capture BOTH the text and URN for the previous conversation before resetting
    const textToFlush = currentTextRef.current
    const urnToFlush = prevUrnRef.current

    // Flush pending save for the PREVIOUS conversation before loading new one
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    if (dirtyRef.current && urnToFlush && userRef.current) {
      void persistDraft(userRef.current.id, urnToFlush, textToFlush)
    }

    // Reset state for new conversation
    setDraft('')
    currentTextRef.current = ''
    dirtyRef.current = false
    setLoaded(false)

    if (!user || !conversationUrn) {
      prevUrnRef.current = conversationUrn
      setLoaded(true)
      return
    }

    // Load from Supabase, fallback to localStorage
    supabase
      .from('drafts')
      .select('text')
      .eq('conversation_urn', conversationUrn)
      .maybeSingle()
      .then(({ data, error }) => {
        // Don't overwrite if conversation changed or user started typing
        if (prevUrnRef.current !== conversationUrn) return
        if (dirtyRef.current) return

        const text = (!error && data?.text)
          ? data.text
          : (localStorage.getItem(DRAFT_PREFIX + conversationUrn) ?? '')
        setDraft(text)
        currentTextRef.current = text
        setLoaded(true)
      })

    // Update previous URN at the END so cleanup always has the right one
    prevUrnRef.current = conversationUrn
  }, [user, conversationUrn])

  const saveDraft = useCallback((text: string) => {
    setDraft(text)
    currentTextRef.current = text
    dirtyRef.current = true

    // Capture URN and user NOW — not when the debounce fires
    const capturedUrn = prevUrnRef.current
    const capturedUser = userRef.current

    if (debounceRef.current) clearTimeout(debounceRef.current)
    debounceRef.current = setTimeout(() => {
      if (capturedUser && capturedUrn) {
        void persistDraft(capturedUser.id, capturedUrn, text)
      }
      dirtyRef.current = false
    }, 500)
  }, [])

  const clearDraft = useCallback(() => {
    // Abort any pending save FIRST
    if (debounceRef.current) {
      clearTimeout(debounceRef.current)
      debounceRef.current = null
    }
    dirtyRef.current = false
    currentTextRef.current = ''
    setDraft('')

    const urn = prevUrnRef.current
    const u = userRef.current
    if (!u || !urn) return
    // Don't pre-remove localStorage — persistDraft('') will remove it
    // AFTER Supabase confirms the delete. Pre-removing used to mean a
    // transient Supabase failure wiped the local fallback with no way
    // to recover.
    void persistDraft(u.id, urn, '')
  }, [])

  return { draft, loaded, saveDraft, clearDraft }
}
